/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useServiceContext } from '../root'
import { useModal } from '../contexts/ModalContext'
import {
  getFileDetail,
  getFilePath,
  getFileValidation,
  getFileDownloadUrl,
  getFileVersionPdfDownloadUrl,
  startCheckingConversion,
  postprocessLocationInfo,
  regenerateDocumentRendition,
  getFsiMultiselectDetailsForActionMenu,
  getFsiMultiselectDetailsForActionMenuV2,
  type MultiselectActionFlags
} from '../services/api/files'
import { verifyFormatting } from '../services/api/etmf'
import { useNavigate } from 'react-router'
import { Document } from '../hooks/useDocumentsListService'
import { VersionsModal } from '../components/modals/VersionsModal'
import SaveAsCRTypeModal from '~/components/manage-wf-modals/SaveAsCRTypeModal'
import RandAPhaseModal from '~/components/manage-wf-modals/R&APhaseModals'
import RejectionReviewersPhaseModal from '~/components/manage-wf-modals/RejectionReviewersModal'
import ApprovalPhaseModal from '~/components/manage-wf-modals/ApprovalPhaseModal'
import ReviewPhaseModal from '~/components/manage-wf-modals/ReviewPhaseModal'
import EditPhaseModal from '~/components/manage-wf-modals/EditPhaseModal'
import AuthorizationPhaseModal from '~/components/manage-wf-modals/AuthorizationPhaseModal'
import DocumentMigrationModal from '../pages/browser/document-migration/DocumentMigrationModal'
import ShareLinkModal from '~/pages/browser/ShareLinkModal'
import CreateQCReviewModal from '../pages/browser/QC-review/CreateQCReviewModal'
import AccessRightsModal from '~/pages/browser/access-rights/AccessRightsModal'
import FsiPropertiesModal from '~/pages/browser/properties/FsiPropertiesModal'
import RejectDocumentsModal from '~/pages/browser/reject/RejectDocuments'
import RemoveDocumentModal from '~/pages/browser/remove-document/RemoveDocumentModal'
import { toast } from '~/services/toast'
import { useTranslation } from 'react-i18next'
import { useRemoveFileFromFavoriteMutation } from './useFiles'
import FolderAndDocumentFavoriteNameModal from '~/pages/browser/FavoriteNameModal'
import FsiReferencesModal from '~/pages/browser/references/FsiReferencesModal'
import AddDocumentModal from '~/pages/browser/add-document/add-new-document/AddDocumentModal'
import SetOwnerModal from '../components/modals/SetOwnerModal'
import SetExpirationDateModal from '../components/modals/SetExpirationDateModal'
import ChangeDocumentTypeModal from '../components/modals/ChangeDocumentTypeModal'
import DocumentExportModal from '../components/modals/DocumentExportModal'
import AddDocChoiceModal from '~/pages/browser/add-document/add-new-document/AddDocChoiceModal'
import FsiTagsModal from '~/pages/settings/tags/TagsModal'
import RenameFolderAndDocumentModal from '~/pages/browser/RenameModal'
import MakeObsoleteModal from '~/pages/browser/MakeObsoleteModal'
import MakeUnObsoleteModal from '~/pages/browser/MakeUnobsoleteModal'
import PermanentDelete from '~/pages/browser/PermanentDeleteModal'
import MoveAndCopyModal from '~/pages/browser/MoveAndCopyModal'
import ReclassifyFsiModal from '~/pages/browser/reclassify/ReclassifyFsiModal'
import GenerateFSTModal from '~/screens/etmf/GenerateFSTModal'
import EnableDisableArtifactsModal from '~/screens/etmf/list/EnableDisableArtifactsModal'
import AddFolderChoiceModal from '../components/modals/AddFolderChoiceModal'
import FolderAndDocumentDetailsModal from '~/components/modals/FolderAndDocumentDetailsModal'
import { useEventBus } from '../hooks/useEventbus'
import { useEsignature } from './useEsignature'
import { validateFileFactory } from '~/services/factory/validateFileFactory'
import TaskCompleteModal from '~/screens/tasks/Actions/complete/TaskCompleteModal'
import {
  TASK_ACTIONS,
  WorkflowType as WorkflowTypeConstants,
  WorkflowStepType as WorkflowStepTypeConstants
} from '~/constants/workflow'
import TaskCancel from '~/screens/tasks/Actions/cancel/TaskCancel'
import { useNextTask } from '~/screens/tasks/Actions/next-task'
import { FsiNotificationsModal } from '../components/FsiNotificationsModal'
import PageWrapper from '../pages/browser/edit-document/PageWrapper'
import Constants from '~/constants/constants'
import AddFolderModal from '~/components/addFolderModal/AddFolderModal'
import { extend } from 'underscore'
import AckDocUploadModal from '~/pages/eos/transfer-media/AckDocUploadModal'

// Types and Interfaces
export interface ActionMenuItem {
  value?: string
  action?: (e?: any) => void
  submenu?: ActionMenuItem[]
  divider?: boolean
  disabled?: boolean
}

interface TaskFlags {
  canReassign?: boolean
  canApprove?: boolean
  canReject?: boolean
  canCancel?: boolean
  canCancelRATask?: boolean
  canRevert?: boolean
}

interface Task {
  id: number
  taskType: string
  workflowStepType?: string
  flags: TaskFlags
}

interface ActionFlags {
  canUploadAndFileToEtmf?: boolean
  canAddFile?: boolean
  canAddFolder?: boolean
  canDoMigration?: boolean
  canExternalAndUserDoSimpleMigration?: boolean
  canManageReferences?: boolean
  canDoExport?: boolean
  canShareLink?: boolean
  canShowAcl?: boolean
  canEditFSTType?: boolean
  canShowActivity?: boolean
  canFileToEtmf?: boolean
  canCreateQCReview?: boolean
  canRemoveDocFromSupGroup?: boolean
  canRequestChange?: boolean
  canReject?: boolean
  canRequestRemoval?: boolean
  canAddAmendment?: boolean
  canShowVersions?: boolean
  canShowProperties?: boolean
  canManageNotifications?: boolean
  canShowReferences?: boolean
  canShowTags?: boolean
  canValidate?: boolean
  canRename?: boolean
  canCopy?: boolean
  canMove?: boolean
  canReclassify?: boolean
  canObsolete?: boolean
  canUnobsolete?: boolean
  canPermanentlyDelete?: boolean
  canDownloadSource?: boolean
  canRegenerate?: boolean
  canManageCR?: boolean
  eosSiteAndCanBeRenamed?: boolean
}

export interface FsiItem {
  id: number
  type: string
  name: string
  favoriteName?: string
  acl: string
  uuid?: string
  systemFolderType?: string
  folderPathText?: string
  templateId?: number
  parentId?: number
  parent?: { id: number; name?: string }
  isFavorite?: boolean
  obsolete?: boolean
  isBeforeApproveStep?: boolean
  workflowType?: string
  actionFlags: ActionFlags
  valid?: boolean
  isConvertable?: boolean
  // Additional properties that can be loaded from API
  title?: string
  docNo?: number
  documentUID?: number
  createdAt?: number
  updatedOn?: number
  updatedAt?: number
  owner?: { id: number; name: string }
  createdBy?: { id: number; name: string }
  updatedBy?: { id: number; name: string }
  documentType?: string
  version?: string
  versionNo?: string
  size?: number
  state?: string
  mimetype?: string
  qcReviewStatus?: string
  certifiedCopy?: boolean
  path?: Array<{ id: number; name: string; acl: string }>
}

interface VersionInfo {
  versionId?: number
  minorVersionId?: number
}

interface UseFsiActionMenuOptions {
  selectedWholeResult?: boolean
  selectedIds?: number[]
  selectedItems?: Array<{ id: number; name?: string; requireExpirationDate?: boolean }>
  totalItemsInSection?: number
  hideView?: boolean
  hideActivity?: boolean
  hideAction?: string
  task?: Task
  parentFolderId?: string | number
  parentId?: number
  isPropertiesSearch?: boolean
  isStagingUser?: boolean
  clinicalFilePlan?: boolean
  deselectAll?: () => void
  filter?: Record<string, unknown>
  meta?: Record<string, unknown>
}

interface MultiselectActions {
  canDownloadZip?: boolean
  canReject?: boolean
  canFileToEtmf?: boolean
  canCreateQCReview?: boolean
  canMove?: boolean
  canCopy?: boolean
  canChangeExpiration?: boolean
  canChangeOwner?: boolean
  canChangeDocType?: boolean
  canShowTags?: boolean
  canObsolete?: boolean
  canUnobsolete?: boolean
  canPermanentlyDelete?: boolean
}

// Constants
const LOADING_MENU_PLACEHOLDER: ActionMenuItem = {
  value: 'LOADING...'
}

const TaskType = {
  USER_REJECTED_TASK: 'USER_REJECTED_TASK',
  USER: 'USER'
} as const

const WorkflowStepType = {
  TRAINING: 'TRAINING'
} as const

const Role = {
  EXTERNAL: 'EXTERNAL',
  USER: 'USER',
  INSPECTOR: 'INSPECTOR',
  ETMF_SITE_USER: 'ETMF_SITE_USER',
  ETMF_STUDY_USER: 'ETMF_STUDY_USER',
  ADMIN: 'ADMIN'
} as const

const FsiType = {
  FOLDER: 'FOLDER',
  PLACEHOLDER: 'PLACEHOLDER',
  REFERENCE: 'REFERENCE'
} as const

const AclLevel = {
  NONE: 'NONE',
  BROWSE: 'BROWSE',
  VIEW: 'VIEW'
} as const

const SystemFolderType = {
  WORKFLOWS: 'WORKFLOWS',
  TEMPLATES: 'TEMPLATES'
} as const

const WorkflowType = {
  CHANGE: 'CHANGE',
  IMPORT: 'IMPORT'
} as const

export const useFsiActionMenu = (
  fsi: Document,
  options: UseFsiActionMenuOptions = {},
  isNotEffectiveVersion: boolean = false,
  versionInfo: VersionInfo = {}
) => {
  const navigateRouter = useNavigate()
  const context = useServiceContext()
  const { api, $rootScope } = context
  const { auth } = context.service
  const validateFile = validateFileFactory()
  const { t: translate } = useTranslation()
  const { emit } = useEventBus()

  // Get feature flags (similar to original JSX)
  const qcReviewEnabled = context?.service?.init?.getFeaturesInfo()?.qcReview?.qcReviewEnabled || false
  const eosFeatureEnabled = context?.service?.init?.getFeaturesInfo()?.eosFeature?.show || false

  // Remove from favorite mutation
  const { t } = useTranslation()
  const { mutate: removeFromFavorite, isPending: isRemovingFavorite } = useRemoveFileFromFavoriteMutation(() => {
    emit('updateFavorites')
    toast.success(t('REMOVE_FAV_SUCCESS', { filename: fsi.name ?? '' }))
    // Emit global event to refresh tables after removing from favorites
    window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'REMOVE_FAVORITE' }))
  })

  const { signAction } = useEsignature()
  const { checkNextTask } = useNextTask()
  const { downloadZipOfPDFFiles } = validateFileFactory()
  // Modal management - use ModalContext directly
  const { showModal, hideModal } = useModal()

  const downloadFsiVersionPdf = async (id: number, versionId: number, minorVersionId: number) => {
    try {
      const resp = await getFileVersionPdfDownloadUrl(id, versionId, true, minorVersionId)
      if (resp && resp.message) {
        toast.info(resp.message)
      }
      const fileInfo = postprocessLocationInfo(resp)
      if (typeof fileInfo.url === 'string') {
        const link = document.createElement('a')
        link.href = fileInfo.url
        link.download = `${fsi.name || `file-${id}`}-v${versionId}.${minorVersionId}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else if (fileInfo.uuidToCheckState) {
        startCheckingConversion(fileInfo.uuidToCheckState, id)
      } else {
        toast.error(translate('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
      }
    } catch (error: any) {
      if (error?.status === 421) {
        toast.info(translate('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
      } else {
        toast.error(error?.body?.message || error?.message || translate('GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER'))
      }
    }
  }

  const loadingMenu = [LOADING_MENU_PLACEHOLDER]
  const optionsLoaded = useRef<string | null>(null)
  const loadingCache = useRef<Set<number>>(new Set()) // Track which FSIs are currently loading

  const [isOpen, setIsOpen] = useState(false)
  const [items, setItems] = useState<ActionMenuItem[]>(loadingMenu)

  // Reset items when FSI changes
  useEffect(() => {
    setItems(loadingMenu)
    optionsLoaded.current = null
    // Clear loading cache for this FSI when it changes
    loadingCache.current.delete(fsi.id)
  }, [fsi.id])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      loadingCache.current.clear()
    }
  }, [])

  // Load file details and path when menu is opened
  const loadFileDetailsAndPath = useCallback(async (fsiItem: Document) => {
    try {
      // Mark as loading
      loadingCache.current.add(fsiItem.id)

      // Load both file details and path in parallel
      const [fileDetails, filePath] = await Promise.all([getFileDetail(fsiItem.id), getFilePath(fsiItem.id)])

      // Merge the loaded details with the existing FSI item
      // Prefer data from API calls over existing data, but keep actionFlags from original
      const enhancedFsi: FsiItem = {
        ...fsiItem,
        ...fileDetails,
        // Ensure actionFlags come from the API response
        actionFlags: fileDetails.actionFlags || fsiItem.actionFlags,
        // Add path information
        path: filePath
      }

      return enhancedFsi
    } catch (error) {
      console.error('❌ Failed to load file details and path:', error)
      // Return original if loading fails, but still try to configure the menu
      return fsiItem
    } finally {
      // Always remove from loading cache
      loadingCache.current.delete(fsiItem.id)
    }
  }, []) // Empty dependency array - this function doesn't depend on external values

  const isWholeResultSelection = options.selectedWholeResult
  const isMultiSelection = options.selectedIds && options.selectedIds.length > 0
  const isMultiButOnlyOneSelection =
    isMultiSelection && options.selectedIds!.length === 1 && options.selectedIds![0] === fsi.id
  const isFsiIncludedInMultiSelection = isMultiSelection && options.selectedIds!.includes(fsi.id)

  // Real navigation implementation using React Router
  const navigate = useCallback(
    (route: string, params?: Record<string, any>) => {
      // Build URL with params
      const urlParams = params ? '?' + new URLSearchParams(params).toString() : ''
      navigateRouter(`/${route}${urlParams}`)
    },
    [navigateRouter]
  )

  // Callback for removing document from group (used in ETMF)
  const doRemoveDocFromGroupCallback = useCallback(
    async (fsiId: number, validation: any) => {
      try {
        await api.etmf.removeDocFromGroup(fsiId, validation)
        // Broadcast table updates (React event bus)
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        window.dispatchEvent(new CustomEvent('bctable:updateSupersededDocs', { detail: { fsiId } }))
        toast.success('Document removed from group')
      } catch (err) {
        console.error('Failed to remove document from the Group', err)
        toast.error('Failed to remove document from group')
      }
    },
    [api.etmf, toast]
  )

  const getCompleteTaskItems = useCallback((task: any, hideAction?: string): ActionMenuItem[] => {
    const menu: ActionMenuItem[] = []

    if (task.taskType === TaskType.USER_REJECTED_TASK) {
      if (task.flags.canRevert && hideAction !== 'taskComplete') {
        menu.push({
          value: 'ACTION_COMMAND_REVERT_CR_TO_EDIT_REVIEW',
          action: (e) => {
            e.preventDefault()
            e.stopPropagation()
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = task.files?.[0]
              ? task.files[0].draftFsi
                ? task.files[0].draftFsi
                : task.files[0].targetFsi
                  ? task.files[0].targetFsi
                  : task.files[0].sourceFsi
              : null

            // Navigate to show-file if there's a viewable file
            if (file && file.type !== FsiType.PLACEHOLDER) {
              navigate(
                `show-file/${file.id}?action=taskComplete&taskId=${task.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file or placeholder
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} />)
            }
          }
        })
      }

      if (task.flags.canCancel) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_CR',
          action: () => {
            showModal(
              <TaskCancel
                task={task}
                onFinished={() => {
                  // Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      if (task.flags.canReassign) {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
          action: () => {
            showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReassign} />)
          }
        })
      }
    } else if (task.taskType === TaskType.USER) {
      if (task.flags.canApprove && hideAction !== 'taskComplete') {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_APPROVE_TASK',
          action: (e) => {
            e.preventDefault()
            e.stopPropagation()
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = task.files?.[0]
              ? task.files[0].draftFsi
                ? task.files[0].draftFsi
                : task.files[0].targetFsi
                  ? task.files[0].targetFsi
                  : task.files[0].sourceFsi
              : null

            // For QC_REVIEW workflows, we need to get the first file from workflow files
            if (task.workflowType === WorkflowTypeConstants.QC_REVIEW) {
              // Load workflow files and navigate to first file with action
              api.workflows
                .getWorkflowFiles(task.workflowId || 0, { pageStart: 0, pageSize: 1 })
                .then((resp) => {
                  const firstFile = resp?.elements?.[0]?.draftFsi
                  if (firstFile) {
                    // For QC workflows, we'd navigate to showFileQC route
                    // For now, use regular show-file route
                    navigate(
                      `show-file-qc/${firstFile.id}?action=taskComplete&taskId=${task.id}&versionId=${(firstFile as any).versionId || -1}&minorVersionId=${(firstFile as any).minorVersionId || 0}`
                    )
                  }
                })
                .catch(() => {
                  // Fallback to modal if API fails
                  showModal(
                    <TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} onFinished={checkNextTask} />
                  )
                })
            } else if (
              file &&
              file.type !== FsiType.PLACEHOLDER &&
              task.workflowStepType !== WorkflowStepTypeConstants.DOWNLOAD_EOS
            ) {
              // Navigate to show-file for regular workflows with viewable files
              navigate(
                `show-file/${file.id}?action=taskComplete&taskId=${task.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file, placeholder, or DOWNLOAD_EOS step
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} onFinished={checkNextTask} />)
            }
          }
        })
      }

      if (task.flags.canCancelRATask) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_TASK',
          action: () => {
            showModal(
              <TaskCancel
                task={task}
                isRATask={true}
                onFinished={() => {
                  // RA Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      if (task.workflowStepType !== WorkflowStepType.TRAINING) {
        if (task.flags.canReject && hideAction !== 'taskReject') {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REJECT_TASK',
            action: (e) => {
              console.log('Reject action clicked')
              e.preventDefault()
              e.stopPropagation()
              // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
              const file = task.files?.[0]
                ? task.files[0].draftFsi
                  ? task.files[0].draftFsi
                  : task.files[0].targetFsi
                    ? task.files[0].targetFsi
                    : task.files[0].sourceFsi
                : null

              // Navigate to show-file if there's a viewable file
              if (file && file.type !== FsiType.PLACEHOLDER) {
                navigate(
                  `show-file/${file.id}?action=taskReject&taskId=${task.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
                )
              } else {
                // Show modal if no file or placeholder
                showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReject} />)
              }
            }
          })
        }

        if (task.flags.canReassign) {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
            action: () => {
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReassign} />)
            }
          })
        }
      }
    }

    return menu
  }, [])

  const configureItems = useCallback(
    (fsi: FsiItem | Document) => {
      let menu: ActionMenuItem[] = []
      const isFolder = fsi.type === FsiType.FOLDER

      if (isFolder) {
        // Upload and File to eTMF
        if (fsi.actionFlags.canUploadAndFileToEtmf) {
          menu.push({
            value: 'UPLOAD_AND_FILE_ACTION_ITEM',
            action: (e: any) => {
              e?.preventDefault?.()
              e?.stopPropagation?.()
              navigate(`upload-and-file/${fsi.id}`)
            }
          })
        }

        // Add Document
        if (fsi.actionFlags.canAddFile) {
          menu.push({
            value: 'BROWSER_ADD_DOCUMENT',
            action: () => {
              const userRole = auth.getRole()
              if (
                userRole &&
                [Role.EXTERNAL, Role.USER, Role.INSPECTOR, Role.ETMF_SITE_USER].includes(userRole as any)
              ) {
                showModal(<AddDocumentModal parentId={fsi.id} />)
              } else {
                showModal(<AddDocChoiceModal parentFsi={fsi} />)
              }
            }
          })
        }

        // Add Folder
        if (fsi.actionFlags.canAddFolder && fsi.uuid !== Constants.file.uuid.FOLDER_STRUCTURE) {
          menu.push({
            value: 'BROWSER_ADD_FOLDER',
            action: () => {
              showModal(<AddFolderChoiceModal parentFsi={fsi} />)
            }
          })
        }

        // Add Folder Structure (special case)
        if (fsi.actionFlags.canAddFolder && fsi.uuid === Constants.file.uuid.FOLDER_STRUCTURE) {
          menu.push({
            value: 'BROWSER_ADD_FOLDER_STRUCTURE_H_TITLE',
            action: () => {
              showModal(<AddFolderModal parentFolderId={fsi.id} {...extend(fsi, { isFolderStructure: true })} />)
            }
          })
        }

        // Simple Document Migration (for external users)
        if (fsi.actionFlags.canExternalAndUserDoSimpleMigration) {
          menu.push({
            value: 'BROWSER_ACTION_UPLOAD_DOCUMENT',
            action: () => {
              showModal(
                <DocumentMigrationModal
                  isOpen={true}
                  onClose={hideModal}
                  folderId={typeof fsi?.id === 'number' ? fsi.id : Number(fsi.id)}
                  externalAndUserSimpleMigration={true}
                  params={{
                    parentFolderType: fsi.systemFolderType,
                    templateId: fsi.templateId,
                    isSiteUpload: false
                  }}
                />
              )
            }
          })
        }

        // Document Migration
        if (fsi.actionFlags.canDoMigration) {
          menu.push({
            value: 'RIGHT_TOP_NAV_DOCUMENT_MIGRATION',
            action: () => {
              showModal(
                <DocumentMigrationModal
                  isOpen={true}
                  onClose={() => {}}
                  folderId={typeof fsi?.id === 'number' ? fsi.id : Number(fsi.id)}
                  params={{
                    parentFolderType: fsi.systemFolderType,
                    templateId: fsi.templateId ? Number(fsi.templateId) : undefined,
                    isSiteUpload: true
                  }}
                />
              )
            }
          })
        }

        // Export to ZIP
        if (fsi.actionFlags.canDoExport) {
          menu.push({
            value: 'BROWSER_EXPORT_TO_ZIP',
            action: () => {
              showModal(<DocumentExportModal show={true} handleClose={hideModal} parentId={fsi.id} />)
            }
          })
        }

        // EOS Admin Acknowledgment Documents (special case for admin in Templates folder)
        if (
          auth.getRole() === Role.ADMIN &&
          eosFeatureEnabled &&
          fsi?.systemFolderType === 'TEMPLATES' &&
          fsi?.folderPathText === '/Templates'
        ) {
          menu.push({
            value: 'ADD_ACK_DOC_MENU',
            action: () => {
              showModal(<AckDocUploadModal />)
            }
          })
        }

        if (menu.length) {
          menu.push({ divider: true })
        }

        // Open Folder
        if (!options.hideView && fsi.acl !== AclLevel.NONE) {
          menu.push({
            value: 'BROWSER_OPEN_FOLDER',
            action: () => {
              // Browser route expects path parameter: /browser/:folderId
              const queryParams = new URLSearchParams({
                type: fsi.systemFolderType || '',
                page: '1'
              })
              navigateRouter(`/browser/${fsi.id}?${queryParams.toString()}`)
            }
          })
        }
      }

      // Handle non-effective version actions
      if (isNotEffectiveVersion) {
        if (fsi.actionFlags.canShareLink) {
          menu.push({
            value: 'BROWSER_ACTION_SHARE_FILE_LINK',
            action: () => {
              showModal(<ShareLinkModal fileDetails={fsi} versionInfo={versionInfo} />)
            }
          })
        }

        menu.push({
          value: 'BROWSER_ACTION_DOWNLOAD',
          action: () => {
            if (versionInfo.versionId != null && versionInfo.minorVersionId != null) {
              toast.info(translate('BROWSER_DOWNLOAD_IS_PREPARING'))
              downloadFsiVersionPdf(fsi.id, versionInfo.versionId, versionInfo.minorVersionId)
            }
          }
        })

        menu.push({
          value: 'BROWSER_ACTION_VERSIONS',
          action: () => {
            showModal(<VersionsModal fsiId={String(fsi.id)} />)
          }
        })

        setItems(menu)
        return
      }

      // Task-specific actions
      if (options.task && options.task.id) {
        const taskOptions = getCompleteTaskItems(options.task, options.hideAction)
        menu = [...menu, ...taskOptions]
        if (taskOptions.length) {
          menu.push({ divider: true })
        }
      }

      // Show File
      if (
        !options.hideView &&
        !fsi.isBeforeApproveStep &&
        ![FsiType.PLACEHOLDER, FsiType.FOLDER].includes(fsi.type as any) &&
        ![AclLevel.NONE, AclLevel.BROWSE].includes(fsi.acl as any)
      ) {
        menu.push({
          value: 'BROWSER_ACTION_SHOW_FILE',
          action: (e?: any) => {
            e?.preventDefault?.()
            e?.stopPropagation?.()
            navigate(`show-file/${fsi.id}`)
          }
        })
      }

      // View Details
      if (fsi.id) {
        menu.push({
          value: isFolder ? 'BROWSER_ACTION_VIEW_DETAILS' : 'BROWSER_ACTION_VIEW_FILE_DETAILS',
          action: () => {
            showModal(<FolderAndDocumentDetailsModal fileDetails={fsi as any} versionInfo={versionInfo} />)
          }
        })
      }

      // Access Rights (ACL)
      if (fsi.actionFlags.canShowAcl) {
        menu.push({
          value: 'BROWSER_ACTION_ACLS',
          action: () => showModal(<AccessRightsModal fsi={fsi} />)
        })
      }

      // Edit Folder Structure Template Type
      if (fsi.actionFlags.canEditFSTType) {
        menu.push({
          value: 'EDIT_FOLDER_STRUC_TEMPL_TYPE_ACTION',
          action: () => {
            showModal(`EditFSTTypeModal - fsi: ${fsi.id}`)
          }
        })
      }

      // For non-folders only
      if (!isFolder) {
        // Generate Folder Structure Templates (Clinical File Plan)
        if (options.clinicalFilePlan) {
          menu.push({
            value: 'BROWSER_ACTION_GENERATE_FLD_STRC_TMPLS',
            action: () => {
              showModal(<GenerateFSTModal fsiId={fsi.id} />)
            }
          })

          menu.push({
            value: 'BROWSER_ACTION_ENABLE_DISABLE_ARTIFACTS',
            action: () => {
              showModal(<EnableDisableArtifactsModal file={fsi} />)
            }
          })
        }

        // Activity
        if (
          fsi.actionFlags.canShowActivity &&
          !options.hideActivity &&
          ![FsiType.PLACEHOLDER, FsiType.REFERENCE].includes(fsi.type as any)
        ) {
          menu.push({
            value: 'BROWSER_ACTION_ACTIVITY',
            action: () => navigate(`FsiActivity/${fsi.id}`)
          })
        }

        // Share Link
        if (fsi.actionFlags.canShareLink && fsi.type !== FsiType.PLACEHOLDER) {
          menu.push({
            value: 'BROWSER_ACTION_SHARE_FILE_LINK',
            action: () => {
              showModal(<ShareLinkModal fileDetails={fsi} versionInfo={versionInfo} />)
            }
          })
        }
      }

      // Favorite actions
      if (fsi.systemFolderType !== SystemFolderType.WORKFLOWS && !fsi.obsolete && fsi.id) {
        if (fsi?.isFavorite) {
          menu.push({
            value: 'BROWSER_ACTION_REMOVE_FROM_FAV',
            action: () => {
              if (!isRemovingFavorite) {
                removeFromFavorite(Number(fsi.id))
              }
            }
          })
        } else {
          menu.push({
            value: 'BROWSER_ACTION_ADD_TO_FAV',
            action: () =>
              showModal(
                <FolderAndDocumentFavoriteNameModal
                  fileDetails={{ ...fsi, favoriteName: fsi?.favoriteName ?? fsi?.name ?? '' }}
                />
              )
          })
        }
      }

      // File to eTMF
      if (fsi.actionFlags.canFileToEtmf) {
        menu.push({
          value: 'BROWSER_ACTION_FILE_DOC_TO_ETMF',
          action: () => {
            navigateRouter(`/show-file/${fsi.id}?action=fileEtmf`)
          }
        })
      }

      // Create QC Review
      if (fsi.actionFlags.canCreateQCReview) {
        menu.push({
          value: 'CREATE_QC_REVIEW',
          action: () =>
            showModal(
              <CreateQCReviewModal
                fsi={fsi}
                options={{
                  selectedIds: options.selectedIds,
                  selectedWholeResult: options.selectedWholeResult,
                  parentFolderId: options.parentFolderId,
                  isPropertiesSearch: options.isPropertiesSearch,
                  filter: options.filter
                }}
              />
            )
        })
      }

      // Remove Document from Group
      if (fsi.actionFlags.canRemoveDocFromSupGroup) {
        menu.push({
          value: 'ADMIN_ACTION_REMOVE_DOC_FROM_GROUP',
          action: async () => {
            try {
              // E-signature validation before removal /component/esignature/useEsignature
              const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_REMOVE_DOC_FROM_GROUP')
              if (validation) {
                await doRemoveDocFromGroupCallback(fsi.id, validation)
              }
            } catch (err: any) {
              // User cancelled or validation failed
              if (err?.message !== 'cancelled') {
                toast.error('E-signature validation failed')
              }
            }
          }
        })
      }

      // Request Change
      if (fsi.actionFlags.canRequestChange) {
        menu.push({
          value:
            fsi.type === FsiType.REFERENCE
              ? 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_REFERENCE'
              : fsi.type === FsiType.PLACEHOLDER
                ? 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_PLACEHOLDER'
                : 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_DOCUMENT',
          action: () => {
            showModal(
              <PageWrapper
                fileId={fsi.id}
                title={
                  fsi.type === FsiType.REFERENCE
                    ? 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_REFERENCE'
                    : fsi.type === FsiType.PLACEHOLDER
                      ? 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_PLACEHOLDER'
                      : 'BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_DOCUMENT'
                }
                onClose={() => hideModal()}
              />
            )
          }
        })
      }

      // Reject Document
      if (fsi.actionFlags.canReject) {
        menu.push({
          value: 'BROWSER_ACTION_REJECT_DOC',
          action: function () {
            showModal(
              <RejectDocumentsModal
                fsiId={fsi.id}
                onClose={() => {
                  hideModal()
                }}
              />
            )
          }
        })
      }

      // Request Removal
      if (fsi.actionFlags.canRequestRemoval) {
        menu.push({
          value:
            fsi.type === FsiType.REFERENCE
              ? 'BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_REFERENCE'
              : fsi.type === FsiType.PLACEHOLDER
                ? 'BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_PLACEHOLDER'
                : 'BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_DOCUMENT',
          action: () => {
            showModal(
              <RemoveDocumentModal
                fsiId={fsi.id}
                onClose={() => {
                  hideModal()
                }}
              />
            )
          }
        })
      }

      // Add New Amendment
      if (fsi.actionFlags.canAddAmendment) {
        menu.push({
          value: 'BROWSER_ACTION_ADD_NEW_AMENDMENT',
          action: () => {
            showModal(<AddDocumentModal parentId={fsi.id} params={{ amend: true, file: fsi }} />)
          }
        })
      }

      // Download
      if (
        ![FsiType.PLACEHOLDER, FsiType.FOLDER].includes(fsi.type as any) &&
        ![AclLevel.NONE, AclLevel.BROWSE, AclLevel.VIEW].includes(fsi.acl as any) &&
        auth.getRole() !== Role.INSPECTOR
      ) {
        const showDownloadSourceMenu =
          fsi.systemFolderType === SystemFolderType.WORKFLOWS || fsi.isConvertable === false
        menu.push({
          value: showDownloadSourceMenu ? 'BROWSER_ACTION_DOWNLOAD_SOURCE_DOC' : 'BROWSER_ACTION_DOWNLOAD',
          action: () => {
            if (showDownloadSourceMenu) {
              validateFile.downloadOriginal(fsi.id)
            } else {
              validateFile.downloadPdf(fsi.id, true)
            }
          }
        })
      }

      // Versions
      if (fsi.actionFlags.canShowVersions) {
        menu.push({
          value: 'BROWSER_ACTION_VERSIONS',
          action: () => {
            showModal(<VersionsModal fsiId={String(fsi.id)} />)
          }
        })
      }

      // Properties
      if (fsi.actionFlags.canShowProperties) {
        menu.push({
          value: 'BROWSER_ACTION_PROPERTIES',
          action: () => {
            showModal(<FsiPropertiesModal fileId={fsi.id} isModal={true} fsiDetails={fsi} />)
          }
        })
      }

      // Notifications (for folders)
      if (isFolder && fsi.actionFlags.canManageNotifications) {
        menu.push({
          value: 'NOTIFICATIONS_FSI_ACTION',
          action: () => {
            showModal(
              <FsiNotificationsModal
                show={true}
                file={{ id: fsi.id, name: fsi.name, type: fsi.type }}
                handleClose={hideModal}
              />
            )
          }
        })
      }

      // Compare with Last Approved (for Change/Import workflows)
      if (fsi.workflowType && (fsi.workflowType === WorkflowType.CHANGE || fsi.workflowType === WorkflowType.IMPORT)) {
        menu.push({
          value: 'BROWSER_ACTION_COMPARE_LAST_APPROVED',
          action: () => {
            showModal(`CompareModal - fsi: ${fsi.id}`)
          }
        })
      }

      // References
      if (fsi.actionFlags.canShowReferences) {
        menu.push({
          value: 'BROWSER_ACTION_REFERENCES',
          action: () => {
            // Only pass FsiItem, not Document
            if ('acl' in fsi && 'actionFlags' in fsi) {
              showModal(<FsiReferencesModal fsi={fsi as FsiItem} />)
            }
          }
        })
      }

      // Tools submenu
      const subMenu: ActionMenuItem[] = []

      // Tags
      if (fsi.actionFlags.canShowTags) {
        subMenu.push({
          value: 'TAGS_TITLE',
          action: () => {
            showModal(<FsiTagsModal fsi={fsi} />)
          }
        })
      }

      // Validate (Content Verification)
      if (fsi.actionFlags.canValidate) {
        // Check for live content verification policy
        const policies = auth.getPolicies()
        const hasLiveContentVerification = policies?.liveContentVerification === true

        if (hasLiveContentVerification) {
          subMenu.push({
            value: 'BROWSER_ACTION_VALIDATE_CONTENT',
            action: async () => {
              try {
                const result = await getFileValidation(fsi.id)
                if (result.value === true) {
                  toast.success(translate('BROWSER_VALIDATE_FILE_IS_VALID'))
                  // Trigger table refresh (window event bus)
                  window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'VALIDATE_FILE' }))
                } else {
                  toast.error(translate('BROWSER_VALIDATE_FILE_IS_INVALID'))
                }
              } catch (error: any) {
                toast.error(error?.message || translate('Validation failed'))
              }
            }
          })
        }
      }

      // Verify Formatting (Clinical File Plan)
      if (options.clinicalFilePlan) {
        subMenu.push({
          value: 'BROWSER_ACTION_VERIFY_FORMATTING',
          action: async () => {
            try {
              await verifyFormatting(fsi.id)
              toast.success('There were no formatting errors found in the File Plan.')
            } catch (error: any) {
              toast.error(error?.message || 'Formatting verification failed')
            }
          }
        })
      }

      // Rename
      if (fsi.actionFlags.canRename) {
        subMenu.push({
          value: fsi.actionFlags.eosSiteAndCanBeRenamed ? 'BROWSER_ACTION_RENAME_SITE' : 'BROWSER_ACTION_RENAME',
          action: () => {
            showModal(<RenameFolderAndDocumentModal fileDetails={fsi} />)
          }
        })
      }

      // Move/Copy
      if (fsi.actionFlags.canCopy && fsi.actionFlags.canMove) {
        const itemText =
          fsi.actionFlags.canCopy && fsi.actionFlags.canMove
            ? 'BROWSER_ACTION_MOVE_COPY'
            : fsi.actionFlags.canCopy
              ? 'BROWSER_ACTION_COPY'
              : 'BROWSER_ACTION_MOVE'

        subMenu.push({
          value: itemText,
          action: () => {
            showModal(
              <MoveAndCopyModal
                fileDetails={fsi}
                options={{
                  canMove: fsi.actionFlags.canMove,
                  canCopy: fsi.actionFlags.canCopy,
                  parentFolderId: fsi.parentId
                }}
              />
            )
          }
        })
      }

      // Reclassify Document
      if (fsi.actionFlags.canReclassify) {
        subMenu.push({
          value: 'BROWSER_ACTION_RECLASSIFY_FSI',
          action: () => {
            showModal(<ReclassifyFsiModal fsiId={fsi.id} />)
          }
        })
      }

      // Make Obsolete
      if (fsi.actionFlags.canObsolete) {
        subMenu.push({
          value: 'BROWSER_ACTION_MAKE_OBSOLETE',
          action: () => {
            showModal(<MakeObsoleteModal fileDetails={fsi} options={options} />)
          }
        })
      }

      // Make Unobsolete
      if (fsi.actionFlags.canUnobsolete) {
        subMenu.push({
          value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
          action: () => {
            showModal(<MakeUnObsoleteModal fileDetails={fsi} options={options} />)
          }
        })
      }

      // Permanent Delete
      if (fsi.actionFlags.canPermanentlyDelete) {
        subMenu.push({
          value: 'BROWSER_ACTION_PERMANENT_DELETE',
          action: () => {
            showModal(<PermanentDelete fsi={fsi} options={options} />)
          }
        })
      }

      // Download Source File
      if (fsi.actionFlags.canDownloadSource && fsi.systemFolderType !== SystemFolderType.WORKFLOWS) {
        subMenu.push({
          value: 'BROWSER_ACTION_DOWNLOAD_SOURCE_DOC',
          action: () => {
            validateFile.downloadOriginal(fsi.id)
          }
        })
      }

      // Regenerate Document Rendition
      if (fsi.actionFlags.canRegenerate) {
        subMenu.push({
          value: 'BROWSER_ACTION_REGENERATE_DOC_RENDITION',
          action: async () => {
            try {
              await regenerateDocumentRendition(fsi.id)
              toast.success('The Regeneration was successful')
              // Trigger table refresh (window event bus)
              window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'REGENERATE_RENDITION' }))
            } catch (error: any) {
              toast.error(error?.message || 'Failed to regenerate document rendition')
            }
          }
        })
      }

      if (subMenu.length > 0) {
        menu.push({ value: 'BROWSER_ACTION_TOOLS', submenu: subMenu })
      }

      // Manage CR submenu
      if (fsi.actionFlags.canManageCR && fsi.type !== FsiType.REFERENCE) {
        const manageSubmenu: ActionMenuItem[] = [
          {
            value: 'BROWSER_ACTION_CR_APPROVERS',
            action: () => {
              showModal(<AuthorizationPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_EDITORS',
            action: () => {
              showModal(<EditPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_REVIEWERS',
            action: () => {
              showModal(<ReviewPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_APPROVERS',
            action: () => {
              showModal(<ApprovalPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_TRAINEES',
            action: () => {
              showModal(<RandAPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_REJECTION_REVIEWERS',
            action: () => {
              showModal(<RejectionReviewersPhaseModal fsi={fsi} />)
            }
          },
          {
            value: 'BROWSER_ACTION_MANAGE_WF_SAVE_AS_WF_TEMPLATE',
            action: () => {
              showModal(<SaveAsCRTypeModal fsi={fsi} />)
            }
          }
        ]
        menu.push({ value: 'BROWSER_ACTION_MANAGE_CRS', submenu: manageSubmenu })
      }

      setItems(menu)
      optionsLoaded.current = getCurrentFsiSelectionCacheKey()
    },
    [
      auth,
      showModal,
      hideModal,
      navigate,
      toast,
      validateFile,
      downloadFsiVersionPdf,
      getCompleteTaskItems,
      doRemoveDocFromGroupCallback,
      options.selectedIds,
      options.selectedWholeResult,
      options.parentFolderId,
      options.task,
      options.hideAction
    ]
  )
  // Extract specific options values instead of full options object to prevent infinite loops

  const configureItemsForMultiSelection = useCallback(
    (
      multiselectactions: MultiselectActions,
      fsi: Document,
      isWholeResultSelection: boolean,
      capturedOptions: UseFsiActionMenuOptions
    ) => {
      const menu: ActionMenuItem[] = []
      const userRole = auth.getRole()
      const isUserRole =
        userRole && [Role.USER, Role.EXTERNAL, Role.ETMF_SITE_USER, Role.ETMF_STUDY_USER].includes(userRole as any)

      if (isUserRole) {
        // For USER roles - only download available, hidden when placeholders selected
        if (multiselectactions.canDownloadZip || isWholeResultSelection) {
          menu.push({
            value: 'BROWSER_ACTION_DOWNLOAD',
            action: () => {
              if (options.selectedIds) {
                downloadZipOfPDFFiles(options.selectedIds, true, options, (a: string, b: string) => {
                  return signAction(a, b)
                })
              }
            }
          })
        }
      } else {
        // For ADMIN and other non-user roles
        if (!isWholeResultSelection && multiselectactions.canReject) {
          menu.push({
            value: 'BROWSER_ACTION_REJECT_DOC',
            action: () => {
              showModal(
                <RejectDocumentsModal
                  fsiId={fsi.id}
                  params={capturedOptions}
                  onClose={() => {
                    hideModal()
                  }}
                />
              )
            }
          })
        }

        // File to eTMF for multiselection, we dont support it in rcm
        if (multiselectactions.canFileToEtmf) {
          menu.push({
            value: 'BROWSER_ACTION_FILE_DOC_TO_ETMF',
            action: () => {
              navigateRouter(`/show-file/${fsi.id}?action=fileEtmf`)
            }
          })
        }

        // Create QC Review for multiselection
        if (multiselectactions.canCreateQCReview && qcReviewEnabled) {
          menu.push({
            value: 'CREATE_QC_REVIEW',
            action: () => {
              console.log('capturedOptions')
              console.log(capturedOptions)
              showModal(
                <CreateQCReviewModal
                  fsi={fsi}
                  options={{
                    ...capturedOptions,
                    selectedItems: capturedOptions.selectedItems?.map((item) => ({
                      id: item.id,
                      name: String(item.name)
                    }))
                  }}
                />
              )
            }
          })
        }

        // Move/Copy for multiselection
        if (multiselectactions.canMove || multiselectactions.canCopy || isWholeResultSelection) {
          const itemText =
            isWholeResultSelection || (multiselectactions.canCopy && multiselectactions.canMove)
              ? 'BROWSER_ACTION_MOVE_COPY'
              : multiselectactions.canCopy
                ? 'BROWSER_ACTION_COPY'
                : 'BROWSER_ACTION_MOVE'

          menu.push({
            value: itemText,
            action: () => {
              showModal(
                <MoveAndCopyModal
                  fileDetails={fsi}
                  options={{
                    ...options,
                    canMove: multiselectactions.canMove,
                    canCopy: multiselectactions.canCopy,
                    selectedWholeResult: isWholeResultSelection
                  }}
                />
              )
            }
          })
        }

        // Download ZIP
        if (multiselectactions.canDownloadZip || isWholeResultSelection) {
          menu.push({
            value: 'BROWSER_ACTION_DOWNLOAD',
            action: () => {
              if (options.selectedIds) {
                downloadZipOfPDFFiles(options.selectedIds, true, options, (a: string, b: string) => {
                  return signAction(a, b)
                })
              }
            }
          })
        }

        // Additional actions for whole result selection
        if (isWholeResultSelection) {
          menu.push({
            value: 'BROWSER_ACTION_MAKE_OBSOLETE',
            action: () => {
              showModal(<MakeObsoleteModal fileDetails={fsi} options={options} />)
            }
          })

          menu.push({
            value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
            action: () => {
              showModal(<MakeUnObsoleteModal fileDetails={fsi} options={options} />)
            }
          })

          menu.push({
            value: 'BROWSER_ACTION_PERMANENT_DELETE',
            action: () => {
              showModal(<PermanentDelete fsi={fsi} options={options} />)
            }
          })

          if (qcReviewEnabled) {
            menu.push({
              value: 'CREATE_QC_REVIEW',
              action: () =>
                showModal(
                  <CreateQCReviewModal
                    fsi={fsi}
                    options={{
                      ...options,
                      selectedItems: options.selectedItems?.map((item) => ({
                        id: item.id,
                        name: String(item.id)
                      }))
                    }}
                  />
                )
            })
          }
        } else {
          // Specific multiselection actions (non-whole result)
          if (multiselectactions.canObsolete) {
            menu.push({
              value: 'BROWSER_ACTION_MAKE_OBSOLETE',
              action: () => {
                showModal(<MakeObsoleteModal fileDetails={fsi} options={options} />)
              }
            })
          }

          if (multiselectactions.canUnobsolete) {
            menu.push({
              value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
              action: () => {
                showModal(<MakeUnObsoleteModal fileDetails={fsi} options={options} />)
              }
            })
          }

          if (multiselectactions.canPermanentlyDelete) {
            menu.push({
              value: 'BROWSER_ACTION_PERMANENT_DELETE',
              action: () => {
                showModal(<PermanentDelete fsi={fsi} options={options} />)
              }
            })
          }
        }

        // Change Expiration Date
        if (multiselectactions.canChangeExpiration) {
          menu.push({
            value: 'SET_EXPIRATION_DATE_ACTION',
            action: () => {
              showModal(
                <SetExpirationDateModal
                  options={{
                    selectedIds: options.selectedIds || [],
                    selectedWholeResult: options.selectedWholeResult,
                    selectedItems: options.selectedItems,
                    parentFolderId:
                      typeof options.parentFolderId === 'number'
                        ? options.parentFolderId
                        : options.parentFolderId
                          ? Number(options.parentFolderId)
                          : undefined,
                    isPropertiesSearch: options.isPropertiesSearch,
                    filter: options.filter,
                    meta: options.meta
                  }}
                />
              )
            }
          })
        }

        // Change Owner
        if (multiselectactions.canChangeOwner) {
          menu.push({
            value: 'SET_OWNER_ACTION',
            action: () => {
              showModal(<SetOwnerModal options={{ selectedIds: options.selectedIds || [] }} />)
            }
          })
        }

        // Change Document Type
        if (multiselectactions.canChangeDocType) {
          menu.push({
            value: 'CHANGE_DOC_TYPE_ACTION',
            action: () => {
              showModal(
                <ChangeDocumentTypeModal
                  options={{
                    selectedIds: options.selectedIds || [],
                    selectedWholeResult: options.selectedWholeResult,
                    parentFolderId:
                      typeof options.parentFolderId === 'number'
                        ? options.parentFolderId
                        : options.parentFolderId
                          ? Number(options.parentFolderId)
                          : undefined,
                    isPropertiesSearch: options.isPropertiesSearch,
                    filter: options.filter,
                    meta: options.meta
                  }}
                />
              )
            }
          })
        }

        // Tags for multiselection
        if (multiselectactions.canShowTags) {
          menu.push({
            value: 'TAGS_TITLE',
            action: () => {
              showModal(<FsiTagsModal fsi={fsi} params={options} />)
            }
          })
        }

        // Properties for multiselection (only for non-whole result selection)
        if (!isWholeResultSelection) {
          menu.push({
            value: 'BROWSER_ACTION_PROPERTIES',
            action: () => {
              showModal(<FsiPropertiesModal fileId={fsi.id} isModal={true} fsiDetails={fsi} options={options} />)
            }
          })
        }
      }

      setItems(menu)
      optionsLoaded.current = getCurrentFsiSelectionCacheKey()
    },
    [auth, showModal, options.selectedIds, options.deselectAll]
  )
  // Extract specific options values instead of full options object to prevent infinite loops

  const getCurrentFsiSelectionCacheKey = useCallback((): string => {
    // Simple cache key based on FSI ID for single selection
    return `fsi-${fsi.id}`
  }, [fsi.id])

  useEffect(() => {
    if (!Number.isInteger(fsi.id)) {
      return
    }

    // Set parent folder ID logic (like original JSX)
    const currentOptions = { ...options }
    currentOptions.parentFolderId =
      currentOptions.parentFolderId ||
      (currentOptions.parentId
        ? currentOptions.parentId
        : fsi.parentId
          ? fsi.parentId
          : fsi.parent
            ? fsi.parent.id
            : undefined)

    // Handle deselect logic for multi-selection (like original JSX)
    if (isOpen && isMultiSelection && !isFsiIncludedInMultiSelection) {
      // If the action menu was opened for file which is not in the current multiselection, deselect all
      if (typeof currentOptions.deselectAll === 'function') {
        currentOptions.deselectAll()
      }
      // Reset whole result selection
      const updatedOptions = { ...currentOptions, selectedWholeResult: false }
    }

    // Always reset cache when fsi.id changes
    if (optionsLoaded.current && !optionsLoaded.current.includes(fsi.id.toString())) {
      optionsLoaded.current = null
    }

    if (isOpen) {
      setItems(loadingMenu)

      // Prevent multiple concurrent loads for the same FSI
      const currentKey = getCurrentFsiSelectionCacheKey()
      optionsLoaded.current = currentKey

      if (
        isWholeResultSelection ||
        (isMultiSelection && !isMultiButOnlyOneSelection && isFsiIncludedInMultiSelection)
      ) {
        // Multi-selection menu - load real multi-selection details like original JSX
        // Capture current options to prevent closure issues
        const capturedOptions = { ...currentOptions }

        if (isWholeResultSelection) {
          // For whole result selection, use enhanced FSI
          const mockMultiselectActions: MultiselectActions = {
            canDownloadZip: true,
            canMove: true,
            canCopy: true,
            canReject: true,
            canFileToEtmf: true,
            canCreateQCReview: true,
            canChangeExpiration: true,
            canChangeOwner: true,
            canChangeDocType: true,
            canShowTags: true,
            canObsolete: true,
            canUnobsolete: true,
            canPermanentlyDelete: true
          }
          configureItemsForMultiSelection(mockMultiselectActions, fsi, true, capturedOptions)
        } else if (currentOptions.selectedIds && currentOptions.selectedIds.length > 0) {
          // For specific file selection, try to load real multi-selection details
          // TODO: Implement real API call like original JSX:
          // api.files.getFsiMultiselectDetailsForActionMenu(currentOptions.parentFolderId, currentOptions.selectedIds)
          //   .then(data => configureItemsForMultiSelection(data.plain(), fsi, false, capturedOptions))

          // Call API based on whether this is property search or folder browsing
          const apiCall = currentOptions.isPropertiesSearch
            ? getFsiMultiselectDetailsForActionMenuV2(currentOptions.selectedIds)
            : currentOptions.parentFolderId
              ? getFsiMultiselectDetailsForActionMenu(
                  typeof currentOptions.parentFolderId === 'number'
                    ? currentOptions.parentFolderId
                    : Number(currentOptions.parentFolderId),
                  currentOptions.selectedIds
                )
              : null

          if (apiCall) {
            apiCall
              .then((data) => {
                configureItemsForMultiSelection(data, fsi, false, capturedOptions)
              })
              .catch((error) => {
                console.error('❌ Failed to load multi-selection details:', error)
                // On error, show minimal menu (download only)
                const fallbackActions: MultiselectActions = {
                  canDownloadZip: true
                }
                configureItemsForMultiSelection(fallbackActions, fsi, false, capturedOptions)
              })
          } else {
            // No parent folder ID - shouldn't happen, but use fallback
            console.warn('⚠️ No parent folder ID for multi-selection, using fallback')
            const fallbackActions: MultiselectActions = {
              canDownloadZip: true
            }
            configureItemsForMultiSelection(fallbackActions, fsi, false, capturedOptions)
          }
        }
      } else {
        // If actionFlags already exist, use them directly without loading details
        // This matches legacy behavior and prevents unnecessary API calls for reports (fsi.id = 0)
        if (fsi.actionFlags) {
          configureItems(fsi)
          return
        }

        // For abstract folders, use empty actionFlags
        if ((fsi as any).isAbstractFolder) {
          configureItems({
            ...fsi,
            actionFlags: {} as ActionFlags
          })
          return
        }

        // Load enhanced FSI data with file details and path for regular items
        loadFileDetailsAndPath(fsi)
          .then((enhancedFsi) => {
            configureItems(enhancedFsi)
          })
          .catch((error) => {
            console.error('Failed to load file details, using original FSI:', error)
            configureItems(fsi)
          })
      }
    }
  }, [
    fsi.id,
    isOpen
    // Removed other dependencies that were causing infinite loops
    // Removed other dependencies that were causing infinite loops
  ])

  return {
    items,
    isOpen,
    setIsOpen
  }
}

export default useFsiActionMenu
